import { TOTAL_FRAMES } from './SceneSections';
import { settings } from '@/config';
import { createLogger } from '@/utils/logger';
import type { ScrollCameraTest } from './ScrollCameraTest';
import type { ScrollManager } from '@/scroll/ScrollManager';
import type { TickState } from '@/types';

/**
 * The opening cinematic: after the preloader clears, the baked camera flies itself
 * smoothly from `startFrame` → `endFrame` (config `settings.intro.autoplay`) with an
 * ease, WITHOUT any scroll input, then holds and hands control to scroll. Scroll then
 * resumes from `endFrame` (via {@link ScrollCameraTest.introStartFraction}) rather than
 * rewinding to 0.
 *
 * Scroll input is locked for the whole fly-in so the viewer just watches; a completion
 * callback fires once it settles (used to reveal the front-end UI).
 */
export class IntroSequence {
  /** True while the fly-in is playing (Experience skips the scroll-camera update then). */
  active = false;

  private elapsed = 0;
  private onDone: (() => void) | null = null;
  private readonly log = createLogger('intro');

  constructor(
    private readonly scrollCamera: ScrollCameraTest,
    private readonly scroll: ScrollManager,
  ) {}

  /** Begin the fly-in. `onComplete` fires after the hold, once scroll has taken over. */
  start(onComplete: () => void): void {
    const cfg = settings.intro.autoplay;
    this.onDone = onComplete;
    if (!cfg.enabled) {
      this.finish();
      return;
    }
    this.active = true;
    this.elapsed = 0;
    this.scroll.lockInput(); // viewer just watches the fly-in
    // Frame the very first frame immediately so there's no 1-frame pop from the bind pose.
    this.scrollCamera.sampleAt(cfg.startFrame / TOTAL_FRAMES);
    this.log.info(`intro fly-in: frame ${cfg.startFrame} → ${cfg.endFrame} over ${cfg.durationSec}s.`);
  }

  update(tick: TickState): void {
    if (!this.active) return;
    const cfg = settings.intro.autoplay;
    this.elapsed += tick.delta;

    const total = cfg.durationSec + cfg.holdSec;
    const t = Math.min(this.elapsed / Math.max(cfg.durationSec, 1e-3), 1);
    const eased = easeInOutCubic(t);
    const frame = cfg.startFrame + (cfg.endFrame - cfg.startFrame) * eased;
    this.scrollCamera.sampleAt(frame / TOTAL_FRAMES);

    if (this.elapsed >= total) this.finish();
  }

  private finish(): void {
    if (!this.active && this.onDone === null) return;
    const cfg = settings.intro.autoplay;
    this.active = false;
    // Scroll now resumes from where the fly-in ended (not frame 0).
    this.scrollCamera.introStartFraction = cfg.endFrame / TOTAL_FRAMES;
    this.scrollCamera.resetSmoothing();
    this.scrollCamera.enabled = true;
    // `force: true` — scroll is still LOCKED here; without it Lenis ignores scrollTo while stopped
    // and the hand-off would leave scroll wherever it was (e.g. 81% after a restart), snapping the
    // camera. (Worked at first load only because scroll was already at 0.)
    this.scroll.scrollTo(0, { immediate: true, force: true });
    this.scroll.unlockInput();
    const done = this.onDone;
    this.onDone = null;
    done?.();
    this.log.info('intro complete — scroll control handed over.');
  }
}

/** Smooth acceleration → deceleration, so the fly-in eases into and out of motion. */
function easeInOutCubic(x: number): number {
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}
